import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Columns, Typography } from "@aviary";
import { AddressInfoFields } from "@shared/components/Fields/AddressInfoFields/AddressInfoFields";
import { BankAccountInfoFields } from "@shared/components/Fields/BankAccountInfoFields/BankAccountInfoFields";
import { BusinessInfoFields } from "@shared/components/Fields/BusinessInfoFields/BusinessInfoFields";
import type {
  BusinessInfoFieldsTypeErrors,
  BusinessInfoFieldsType,
} from "@shared/components/Fields/BusinessInfoFields/BusinessInfoFields";
import { PersonalInfoFields } from "@shared/components/Fields/PersonalInfoFields/PersonalInfoFields";
import type {
  PersonalInfoFieldsType,
  PersonalInfoFieldsErrors,
} from "@shared/components/Fields/PersonalInfoFields/PersonalInfoFields";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import type {
  AddressInformationErrors,
  AddressInformationData,
} from "@shared/types/fields/addressInformationTypes";
import type {
  BankInformationErrors,
  BankAccountInfoFieldValues,
} from "@shared/types/fields/bankInformationTypes";
import { OAuthMarginPercentage } from "@unauthenticated/oauth/components/OAuthOnboardingFlow/OAuthBankingStep/OAuthBankingInformation/OAuthBankingForm/OAuthMarginPercentage/OAuthMarginPercentage";
import { l } from "@unauthenticated/oauth/locales/i18n";

import {
  isAddressInfoValid,
  isBankAccountInfoValid,
  isBusinessInfoValid,
  isPersonalInfoValid,
} from "./oauthBankFormValidation";

import * as styles from "./OAuthBankingForm.styles";

interface FormValues {
  marginPercentage: string;
  addressInfo: AddressInformationData;
  personalInfo: PersonalInfoFieldsType;
  businessInfo: BusinessInfoFieldsType;
  bankAccountInfo: BankAccountInfoFieldValues;
}

interface FormErrors {
  addressInfo: AddressInformationErrors["fields"];
  personalInfo: PersonalInfoFieldsErrors;
  businessInfo: BusinessInfoFieldsTypeErrors;
  bankAccountInfo: BankInformationErrors["fields"];
}

type Fieldset =
  | "marginPercentage"
  | "addressInfo"
  | "personalInfo"
  | "businessInfo"
  | "bankAccountInfo";

type FieldType =
  | string
  | AddressInformationData
  | PersonalInfoFieldsType
  | BusinessInfoFieldsType
  | BankAccountInfoFieldValues;

interface Props {
  values: FormValues;
  profitMarginIncrements: number[];
  isFullSsnRequired: boolean;
  isUpdating: boolean;
  formErrors: FormErrors;
  onChange: (fieldSet: Fieldset) => (fieldType: FieldType) => void;
  onGoBack: () => void;
  onFinishLater: () => void;
  onComplete: () => void;
}

const OAuthBankingForm = ({
  values,
  profitMarginIncrements,
  isFullSsnRequired,
  isUpdating,
  formErrors,
  onChange,
  onGoBack,
  onFinishLater,
  onComplete,
}: Props) => {
  const { t } = useTranslation();
  const { isUS } = useSharedGlobalConfig();
  const [isSSNValid, setIsSSNValid] = useState(false);

  const disabled =
    !values.marginPercentage ||
    !isBusinessInfoValid(values.businessInfo, isUS) ||
    !isPersonalInfoValid(values.personalInfo) ||
    !isAddressInfoValid(values?.addressInfo) ||
    !isBankAccountInfoValid(values.bankAccountInfo) ||
    !isSSNValid;

  return (
    <form onSubmit={e => e.preventDefault()} css={styles.form}>
      <Typography css={styles.blurb}>{t(l.oauth.congratulationsOnBecomingAProfit)}</Typography>
      <div>
        <OAuthMarginPercentage
          value={values?.marginPercentage}
          onChange={onChange("marginPercentage")}
          profitMarginIncrements={profitMarginIncrements}
        />
      </div>
      <div>
        <Typography type="h4" sizeOverride="h5">
          {t(l.oauth.financialAddress)}
        </Typography>
        <AddressInfoFields
          addressInformationData={values?.addressInfo}
          addressInformationErrors={{ fields: formErrors?.addressInfo }}
          handleSetAddressInformation={onChange("addressInfo")}
          isFullWidth={false}
        />
      </div>
      <div>
        <Typography type="h4" sizeOverride="h5">
          {t(l.oauth.businessInfo)}
        </Typography>
        <Columns>
          <PersonalInfoFields
            values={values?.personalInfo}
            errors={formErrors?.personalInfo}
            onChange={onChange("personalInfo")}
            isFullSsnRequired={isFullSsnRequired}
            hideNameFields
            setIsSSNValid={setIsSSNValid}
          />
        </Columns>
        <div css={styles.personalInfo}>
          <BusinessInfoFields
            values={values?.businessInfo}
            errors={formErrors?.businessInfo}
            onChange={onChange("businessInfo")}
            readonly={false}
          />
        </div>
      </div>
      <div>
        <Typography type="h4" sizeOverride="h5">
          {t(l.oauth.payoutBankInfo)}
        </Typography>
        <BankAccountInfoFields
          values={values.bankAccountInfo}
          errors={formErrors?.bankAccountInfo}
          onChange={onChange("bankAccountInfo")}
        />
      </div>
      <div css={styles.buttonWrapper}>
        <Button css={styles.goBack} isColor="system" isOutlined onClick={onGoBack}>
          {t(l.oauth.goBackToMargin)}
        </Button>

        <div css={styles.proceedButtons}>
          <Button
            css={styles.button}
            intention="primary"
            isOutlined
            disabled={isUpdating}
            onClick={onFinishLater}
          >
            {t(l.oauth.finishLater)}
          </Button>
          <Button
            css={styles.button}
            intention="primary"
            disabled={disabled}
            isLoading={isUpdating}
            onClick={onComplete}
          >
            {t(l.oauth.complete)}
          </Button>
        </div>
      </div>
    </form>
  );
};

export { OAuthBankingForm, type FormValues, type FormErrors, type Fieldset };
